import React from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Tenancy } from '../../../types/tenancyTypes';

export interface MaintenanceRequestFormData {
  listingId: string;
  issueDescription: string;
  photos: FileList;
  category: string;
}

interface MaintenanceRequestFormProps {
  tenancies: Tenancy[];
  isSubmitting: boolean;
  photoPreviews: string[];
  handleRemovePhoto: (index: number) => void;
  onSubmit: (data: MaintenanceRequestFormData) => void;
  register: UseFormRegister<MaintenanceRequestFormData>;
  errors: FieldErrors<MaintenanceRequestFormData>;
  handleSubmit: any;
  maintenanceCategories: string[];
}

const MaintenanceRequestForm: React.FC<MaintenanceRequestFormProps> = ({
  tenancies,
  isSubmitting,
  photoPreviews,
  handleRemovePhoto,
  onSubmit,
  register,
  errors,
  handleSubmit,
  maintenanceCategories
}) => {
  return (
    <div className="bg-gray-50 p-6 rounded-lg mb-6 border border-gray-200">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="listingId"
            className="block text-sm font-medium text-gray-700"
          >
            Property
          </label>
          <select
            id="listingId"
            {...register('listingId', {
              required: 'Please select a property'
            })}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            <option value="">Select a property</option>
            {tenancies.map((tenancy) => (
              <option key={tenancy.id} value={tenancy.listingId}>
                {tenancy.propertyInfo.title}
              </option>
            ))}
          </select>
          {errors.listingId && (
            <p className="mt-2 text-sm text-red-600">
              {errors.listingId.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="category"
            className="block text-sm font-medium text-gray-700"
          >
            Category
          </label>
          <select
            id="category"
            {...register('category', { required: true })}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            {maintenanceCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="issueDescription"
            className="block text-sm font-medium text-gray-700"
          >
            Describe the issue
          </label>
          <textarea
            id="issueDescription"
            {...register('issueDescription', { required: true })}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            rows={4}
          ></textarea>
        </div>
        <div>
          <label
            htmlFor="photos"
            className="block text-sm font-medium text-gray-700"
          >
            Add Photos (optional)
          </label>
          <div className="mt-1 flex items-center justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <PhotoIcon
                className="mx-auto h-12 w-12 text-gray-400"
                aria-hidden="true"
              />
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="photos"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                >
                  <span>Upload files</span>
                  <input
                    id="photos"
                    type="file"
                    {...register('photos')}
                    multiple
                    accept="image/*"
                    className="sr-only"
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-4">
            {photoPreviews.map((src, i) => (
              <div key={i} className="relative">
                <img
                  src={src}
                  alt="preview"
                  className="w-24 h-24 object-cover rounded-md"
                />
                <button
                  type="button"
                  onClick={() => handleRemovePhoto(i)}
                  className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full shadow-md hover:bg-red-700 transition-colors"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 disabled:bg-gray-400"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MaintenanceRequestForm;
